import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Trash2, Archive, Leaf, Sprout } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { PlantDetailModal } from '../components/PlantDetailModal';
import * as localStore from '../services/localPlantStore';
import { API_BASE_URL } from '../utils/apiBase';

const REASON_LABELS = {
  died: 'Died',
  sold: 'Sold',
  given_away: 'Given Away',
  discarded: 'Discarded',
  other: 'Other',
};

export default function ArchivePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [archivedPlants, setArchivedPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const isLocalMode = localStorage.getItem('local_mode') === 'true';

  useEffect(() => {
    loadArchived();
  }, []);

  const loadArchived = async () => {
    setLoading(true);
    try {
      let archived;
      if (isLocalMode) {
        const all = await localStore.getAllPlants();
        archived = all.filter(p => p.archived);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const response = await axios.get(`${API_BASE_URL}/api/plants`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        archived = response.data.filter(p => p.archived);
      }
      // Sort newest archived first so the most recent ones are at the top.
      archived.sort((a, b) => {
        const ta = a.archived_at ? new Date(a.archived_at).getTime() : 0;
        const tb = b.archived_at ? new Date(b.archived_at).getTime() : 0;
        return tb - ta;
      });
      setArchivedPlants(archived);
    } catch (error) {
      console.error('Failed to load archived plants:', error);
      toast.error('Failed to load archived plants');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (plantId) => {
    try {
      if (isLocalMode) {
        await localStore.restorePlant(plantId);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.post(`${API_BASE_URL}/api/plants/${plantId}/restore`, {}, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
      }
      toast.success('Plant restored to library');
      setArchivedPlants(prev => prev.filter(p => p.plant_id !== plantId));
    } catch (error) {
      console.error('Failed to restore plant:', error);
      toast.error('Failed to restore plant');
    }
  };

  const handleDelete = async (plantId) => {
    if (!window.confirm('Permanently delete this plant? This cannot be undone.')) return;
    try {
      if (isLocalMode) {
        await localStore.deletePlant(plantId);
        await localStore.deleteAllPhotos(plantId);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.delete(`${API_BASE_URL}/api/plants/${plantId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
      }
      toast.success('Plant permanently deleted');
      setArchivedPlants(prev => prev.filter(p => p.plant_id !== plantId));
    } catch (error) {
      console.error('Failed to delete plant:', error);
      toast.error('Failed to delete plant');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(82, 55%, 97%)' }}>
      {/* App-branded top row — matches the "My Plants" logo/title from the
          library so the archive doesn't look like a stranded sub-page.
          Clickable back to /library, keyboard-accessible. */}
      <div
        className="border-b"
        style={{ borderColor: 'hsl(143, 28%, 90%)', backgroundColor: 'rgba(255,255,255,0.6)' }}
        role="banner"
      >
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div
            className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0"
            onClick={() => navigate('/library')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/library'); } }}
            role="link"
            tabIndex={0}
            aria-label={t('goToMainLibrary') || 'Go to main library page'}
            data-testid="archive-app-home-link"
          >
            <Sprout className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" style={{ color: 'hsl(143, 28%, 25%)' }} aria-hidden="true" />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-display font-bold whitespace-nowrap" style={{ color: 'hsl(143, 28%, 25%)' }}>
              {t('myPlants') || 'My Plants'}
            </h1>
          </div>
        </div>
      </div>

      {/* Archive header — narrower, purely a section label now that the app
          branding lives above. */}
      <div className="sticky top-0 z-40 glassmorphism border-b" style={{ borderColor: 'hsl(143, 28%, 90%)' }} role="region" aria-label="Archived plants">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/library')} aria-label="Back to library" data-testid="archive-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Archive className="w-6 h-6" style={{ color: 'hsl(143, 28%, 25%)' }} aria-hidden="true" />
            <h1 className="text-xl md:text-2xl font-display font-bold" style={{ color: 'hsl(143, 28%, 25%)' }}>
              Archived Plants
            </h1>
            {archivedPlants.length > 0 && (
              <span className="ml-2 text-sm text-gray-500">({archivedPlants.length})</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 rounded-full" style={{ borderColor: 'hsl(143, 28%, 80%)', borderTopColor: 'hsl(143, 28%, 35%)' }} />
          </div>
        ) : archivedPlants.length === 0 ? (
          <div className="text-center py-16" data-testid="archive-empty">
            <Archive className="w-16 h-16 mx-auto mb-4" style={{ color: 'hsl(143, 28%, 70%)' }} />
            <h2 className="text-xl font-semibold" style={{ color: 'hsl(143, 28%, 35%)' }}>No Archived Plants</h2>
            <p className="mt-2 text-sm text-gray-500">Plants you archive will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="archived-plants-list">
            {archivedPlants.map(plant => (
              <div
                key={plant.plant_id}
                className="bg-white rounded-xl border p-4 flex items-center gap-4 transition-colors hover:bg-gray-50"
                style={{ borderColor: 'hsl(143, 28%, 90%)' }}
                data-testid={`archived-plant-${plant.plant_id}`}
              >
                {/* Plant icon/photo placeholder */}
                <div className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
                  <Leaf className="w-6 h-6" style={{ color: 'hsl(143, 28%, 40%)', opacity: 0.5 }} />
                </div>

                {/* Plant info */}
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelectedPlant(plant)}>
                  <p className="font-medium text-sm truncate" style={{ color: 'hsl(143, 28%, 20%)' }}>
                    {plant.common_name || 'Unnamed'}
                  </p>
                  {plant.scientific_name && (
                    <p className="text-xs text-gray-400 italic truncate">{plant.scientific_name}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: 'hsl(0, 0%, 93%)', color: 'hsl(0, 0%, 40%)' }}
                    >
                      {REASON_LABELS[plant.archive_reason] || plant.archive_reason || 'Archived'}
                    </span>
                    {plant.archived_at && (
                      <span className="text-[10px] text-gray-400">{formatDate(plant.archived_at)}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRestore(plant.plant_id)}
                    aria-label={`Restore ${plant.common_name}`}
                    title="Restore to library"
                    data-testid={`restore-btn-${plant.plant_id}`}
                  >
                    <RotateCcw className="w-4 h-4" style={{ color: 'hsl(143, 28%, 35%)' }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(plant.plant_id)}
                    aria-label={`Permanently delete ${plant.common_name}`}
                    title="Delete permanently"
                    data-testid={`delete-archived-btn-${plant.plant_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Detail modal */}
      {selectedPlant && (
        <PlantDetailModal
          plant={selectedPlant}
          onClose={() => setSelectedPlant(null)}
          onEdit={() => {}}
          onDelete={async (plantId) => { await handleDelete(plantId); setSelectedPlant(null); }}
          onPlantAdded={loadArchived}
          isLocalMode={isLocalMode}
        />
      )}
    </div>
  );
}
